<template>
  <v-dialog
    v-model="dialogVisible"
    fullscreen
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card>
      <!-- App Bar Header -->
      <v-app-bar
        color="primary"
        dark
        flat
        fixed
      >
        <v-toolbar-title>
          <v-icon left>mdi-package-variant</v-icon>
          {{ $t(`item.title.${edit ? 'update' : 'create'}`, { name: item.name }) }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-app-bar>

      <!-- Form Content -->
      <v-container fluid class="mt-16 pa-8">
        <v-row justify="center">
          <v-col cols="12" lg="10" xl="8">
            <v-form ref="itemForm" v-model="valid" lazy-validation>
              <v-card outlined>
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-information</v-icon>
                  {{ $t('item.label.basic_information') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-row>
                    <!-- Item Name -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        ref="inputName"
                        v-model="form.name"
                        :label="$t('item.label.name')"
                        :rules="[rules.required]"
                        outlined
                        dense
                        required
                      ></v-text-field>
                    </v-col>

                    <!-- Category -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.category"
                        :items="categories"
                        item-text="name"
                        item-value="id"
                        :label="$t('item.label.category')"
                        :placeholder="$t('item.placeholder.category')"
                        :rules="[rules.required]"
                        outlined
                        dense
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Description -->
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.description"
                        :label="$t('item.label.description')"
                        outlined
                        dense
                        rows="3"
                        auto-grow
                      ></v-textarea>
                    </v-col>

                    <!-- Sellable Checkbox -->
                    <v-col cols="12">
                      <v-checkbox
                        v-model="form.sellable"
                        :label="$t('item.placeholder.sellable')"
                        color="primary"
                      ></v-checkbox>
                    </v-col>

                    <!-- Sold By -->
                    <v-col cols="12" md="6">
                      <v-radio-group
                        v-model="form.sold_by"
                        :label="$t('item.label.sold_by.label')"
                        row
                      >
                        <v-radio
                          :disabled="!form.sellable"
                          label="each"
                          :value="$t('item.label.sold_by.each')"
                        ></v-radio>
                        <v-radio
                          :disabled="!form.sellable"
                          label="measure"
                          :value="$t('item.label.sold_by.weight')"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>

                    <!-- Item Type -->
                    <v-col cols="12" md="6">
                      <v-radio-group
                        v-model="form.item_type"
                        :label="$t('item.label.item_type')"
                        :rules="[rules.required]"
                        row
                      >
                        <v-radio
                          label="product"
                          value="product"
                        >
                          <template v-slot:label>
                            <span>{{ $t('item.label.type.product') }}</span>
                          </template>
                        </v-radio>
                        <v-radio
                          label="service"
                          value="service"
                        >
                          <template v-slot:label>
                            <span>{{ $t('item.label.type.service') }}</span>
                          </template>
                        </v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Pricing & Supplier Section -->
              <v-card outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-currency-usd</v-icon>
                  {{ $t('item.label.pricing_supplier') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-row>
                    <!-- Supplier -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.supplier"
                        :items="suppliers"
                        item-text="name"
                        item-value="id"
                        :label="$t('item.label.supplier')"
                        :placeholder="$t('item.placeholder.supplier')"
                        outlined
                        dense
                        clearable
                      ></v-autocomplete>
                    </v-col>

                    <!-- Purchase Price -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model.number="form.purchase_price"
                        :label="$t('item.label.purchase_price')"
                        outlined
                        dense
                        type="number"
                        min="0"
                        prefix="XAF"
                      ></v-text-field>
                    </v-col>

                    <!-- Minimum Price -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model.number="form.minimum_price"
                        :label="$t('item.label.minimum_price')"
                        :hint="$t('item.label.minimum_price_hint')"
                        persistent-hint
                        outlined
                        dense
                        type="number"
                        min="0"
                        prefix="XAF"
                      ></v-text-field>
                    </v-col>

                    <!-- Unit Type -->
                    <v-col cols="12" md="6">
                      <v-combobox
                        v-model="form.unit_type"
                        :items="unitTypes"
                        item-text="value"
                        item-value="value"
                        :label="$t('item.label.unit_type')"
                        outlined
                        dense
                      ></v-combobox>
                    </v-col>

                    <!-- Barcode -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.barcode"
                        :label="$t('item.label.barcode')"
                        :placeholder="$t('item.placeholder.barcode')"
                        outlined
                        dense
                        append-icon="mdi-barcode-scan"
                      ></v-text-field>
                    </v-col>

                    <!-- Reorder Level -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.reorder_level"
                        :label="$t('item.label.reorder_level')"
                        :placeholder="$t('item.placeholder.reorder_level')"
                        outlined
                        dense
                        type="number"
                        min="0"
                      ></v-text-field>
                    </v-col>

                    <!-- Optimal Order -->
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.optimal_order"
                        :label="$t('item.label.optimal_order')"
                        :placeholder="$t('item.placeholder.optimal_order')"
                        outlined
                        dense
                        type="number"
                        min="0"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Inventory Tracking -->
              <v-card outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-warehouse</v-icon>
                  {{ $t('item.label.inventory') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-row align="center">
                    <v-col>
                      <span class="text-subtitle-1">{{ $t('item.label.track') }}</span>
                    </v-col>
                    <v-col cols="auto">
                      <v-switch
                        v-model="form.track"
                        color="primary"
                        inset
                      ></v-switch>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Visual Section -->
              <v-card outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-palette</v-icon>
                  {{ $t('item.label.visual') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-row>
                    <v-col cols="12">
                      <v-radio-group v-model="form.visual_type" row>
                        <v-radio label="colour" value="colour">
                          <template v-slot:label>
                            <span>Colour</span>
                          </template>
                        </v-radio>
                        <v-radio label="image" value="image">
                          <template v-slot:label>
                            <span>{{ $t('item.label.image') }}</span>
                          </template>
                        </v-radio>
                      </v-radio-group>
                    </v-col>

                    <!-- Color Palette -->
                    <v-col v-if="form.visual_type === 'colour'" cols="12" md="6">
                      <v-sheet outlined rounded class="pa-4">
                        <div class="text-subtitle-2 mb-3">{{ $t('item.label.select_color') }}</div>
                        <v-row dense>
                          <v-col
                            v-for="(colour, index) in predefineColours"
                            :key="index"
                            cols="auto"
                          >
                            <v-btn
                              fab
                              small
                              :color="colour.colour"
                              @click="setColour(colour)"
                              :outlined="form.colour !== colour.colour"
                              elevation="2"
                            >
                              <v-icon v-if="colour.colour === form.colour" color="white">
                                mdi-check
                              </v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-sheet>
                    </v-col>

                    <!-- Image Upload -->
                    <v-col v-else cols="12" md="6">
                      <v-sheet outlined rounded class="pa-4">
                        <div class="text-subtitle-2 mb-3">{{ $t('item.label.upload_image') }}</div>
                        <v-file-input
                          v-model="imageFile"
                          :disabled="form.visual_type !== 'image'"
                          accept="image/*"
                          prepend-icon="mdi-camera"
                          :label="$t('item.label.select_image')"
                          show-size
                          outlined
                          dense
                          @change="handleImageUpload"
                        ></v-file-input>
                        <v-img
                          v-if="form.image"
                          :src="form.image"
                          max-height="200"
                          max-width="200"
                          class="mt-2"
                          contain
                        >
                          <template v-slot:placeholder>
                            <v-row
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <v-progress-circular
                                indeterminate
                                color="grey lighten-5"
                              ></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Variants Section -->
              <v-card outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-format-list-bulleted-square</v-icon>
                  {{ $t('variant.name') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-alert
                    type="info"
                    text
                    dense
                    class="mb-4"
                  >
                    {{ $t('variant.message.guide') }}
                  </v-alert>

                  <v-row>
                    <v-col cols="auto">
                      <v-btn
                        v-if="edit && item.variants && item.variants.length"
                        color="error"
                        outlined
                        @click="deleteAllVariants"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        {{ $t('button.delete') }}
                      </v-btn>
                    </v-col>
                    <v-col cols="auto">
                      <v-btn
                        color="success"
                        outlined
                        @click="toggleVariantDialog(true)"
                      >
                        <v-icon left>
                          {{ item.variants && item.variants.length ? 'mdi-pencil' : 'mdi-plus' }}
                        </v-icon>
                        {{ item.variants && item.variants.length ? $t('variant.button.edit_variants') : $t('variant.button.add_variants') }}
                      </v-btn>
                    </v-col>
                    <v-col v-if="form.variants.length" cols="auto">
                      <v-chip
                        color="primary"
                        label
                        close
                        @click:close="form.variants = []"
                      >
                        <v-icon left>mdi-alert-circle</v-icon>
                        {{ $t('item.message.variant_modified') }}
                      </v-chip>
                    </v-col>
                  </v-row>

                  <!-- Variants Table -->
                  <v-data-table
                    v-if="item.variants && item.variants.length"
                    :headers="variantHeaders"
                    :items="item.variants"
                    :items-per-page="10"
                    class="elevation-1 mt-4"
                    dense
                  >
                    <template v-slot:[`item.unit_price`]="{ item }">
                      <v-edit-dialog
                        :return-value.sync="item.unit_price"
                        @save="updateEditedItemVariant(item.id, 'unit_price')"
                      >
                        {{ item.unit_price }}
                        <template v-slot:input>
                          <v-text-field
                            v-model="item.unit_price"
                            :label="$t('item.label.unit_price')"
                            single-line
                            type="number"
                          ></v-text-field>
                        </template>
                      </v-edit-dialog>
                    </template>

                    <template v-slot:[`item.purchase_price`]="{ item }">
                      <v-edit-dialog
                        :return-value.sync="item.purchase_price"
                        @save="updateEditedItemVariant(item.id, 'purchase_price')"
                      >
                        {{ item.purchase_price }}
                        <template v-slot:input>
                          <v-text-field
                            v-model="item.purchase_price"
                            :label="$t('item.label.purchase_price')"
                            single-line
                            type="number"
                          ></v-text-field>
                        </template>
                      </v-edit-dialog>
                    </template>

                    <template v-slot:[`item.barcode`]="{ item }">
                      <v-edit-dialog
                        :return-value.sync="item.barcode"
                        @save="updateEditedItemVariant(item.id, 'barcode')"
                      >
                        {{ item.barcode }}
                        <template v-slot:input>
                          <v-text-field
                            v-model="item.barcode"
                            :label="$t('item.label.barcode')"
                            single-line
                          ></v-text-field>
                        </template>
                      </v-edit-dialog>
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn
                        icon
                        color="error"
                        small
                        @click="itemVariantDelete(item.id)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>

              <!-- Modifiers Section -->
              <v-card v-if="modifiers.length > 0" outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-tune</v-icon>
                  {{ $t('modifier.name') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-row
                    v-for="(modifier, index) in modifiers"
                    :key="modifier.id"
                    align="center"
                  >
                    <v-col>
                      <span class="text-body-1">{{ modifier.name }}</span>
                    </v-col>
                    <v-col cols="auto">
                      <v-switch
                        v-model="modifier.selected"
                        color="primary"
                        inset
                        @change="updateItemModifier($event, modifier)"
                      ></v-switch>
                    </v-col>
                    <v-col v-if="index < modifiers.length - 1" cols="12">
                      <v-divider></v-divider>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Taxes Section -->
              <v-card v-if="taxes.length" outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-percent</v-icon>
                  {{ $t('tax.name') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-row
                    v-for="(tax, index) in taxes"
                    :key="tax.id"
                    align="center"
                  >
                    <v-col>
                      <span class="text-body-1">{{ tax.name }}</span>
                    </v-col>
                    <v-col cols="auto">
                      <v-switch
                        v-model="tax.selected"
                        color="primary"
                        inset
                        @change="updateItemTax($event, tax)"
                      ></v-switch>
                    </v-col>
                    <v-col v-if="index < taxes.length - 1" cols="12">
                      <v-divider></v-divider>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Price Lists Section -->
              <v-card outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-format-list-numbered</v-icon>
                  {{ $t('item.label.price_list_prices') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">{{ $t('price_list.label.description') }}</th>
                          <th class="text-left">{{ $t('item.label.selling_price') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(priceList, index) in form.price_lists" :key="priceList.id">
                          <td>{{ priceList.name }}</td>
                          <td>
                            <v-text-field
                              v-model.number="form.price_lists[index].value"
                              dense
                              outlined
                              type="number"
                              min="0"
                              prefix="XAF"
                              @change="priceList.id === 1 ? setSellingPrice($event) : (() => {})"
                            ></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>

              <!-- Store Prices Section -->
              <v-card outlined class="mt-4">
                <v-card-title class="text-h6 primary--text">
                  <v-icon left color="primary">mdi-store</v-icon>
                  {{ $t('item.label.store_prices') }}
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                  <v-simple-table>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ $t('warehouse.label.store') }} ({{ $t('warehouse.label.warehouse') }})
                          </th>
                          <th class="text-left">{{ $t('item.label.selling_price') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(department, index) in form.departments" :key="department.id">
                          <td>{{ department.name }} ({{ department.wh }})</td>
                          <td>
                            <v-text-field
                              v-model.number="form.departments[index].selling_price"
                              :disabled="!department.wh"
                              dense
                              outlined
                              type="number"
                              min="0"
                              prefix="XAF"
                            ></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-form>
          </v-col>
        </v-row>
      </v-container>

      <!-- Fixed Bottom Actions -->
      <v-card
        flat
        tile
        class="fixed-bottom-actions"
      >
        <v-divider></v-divider>
        <v-card-actions class="justify-center pa-4">
          <v-btn
            color="error"
            outlined
            large
            @click="handleClose"
          >
            <v-icon left>mdi-close</v-icon>
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            large
            :disabled="!edited"
            :loading="loading"
            @click="submitForm"
          >
            <v-icon left>mdi-content-save</v-icon>
            {{ $t(`item.button.${edit ? 'update' : 'create'}`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-card>

    <!-- Variant Dialog -->
    <variant-dialog-vuetify
      :display="variantDialog"
      @close="toggleVariantDialog"
      @created="newVariant"
      @updated="updatedVariant"
      :item="item"
      :prev-variants="form.variants"
    ></variant-dialog-vuetify>
  </v-dialog>
</template>

<script>
import VariantDialogVuetify from './VariantDialogVuetify.vue'

export default {
  name: 'CreateEditItemVuetify',
  components: { VariantDialogVuetify },
  props: {
    display: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data () {
    return {
      valid: false,
      variantDialog: false,
      categories: [],
      suppliers: [],
      modifiers: [],
      taxes: [],
      itemModifiers: [],
      itemTaxes: [],
      imageFile: null,
      editedItemVariant: {
        id: '',
        module: ''
      },
      snapshot: {},
      form: {
        name: '',
        description: '',
        unit_price: null,
        purchase_price: null,
        minimum_price: null,
        unit_type: null,
        item_type: 'product',
        visual_type: 'colour',
        sold_by: 'each',
        image: '',
        colour: '',
        barcode: '',
        sellable: true,
        reorder_level: null,
        optimal_order: null,
        category: null,
        supplier: null,
        price_lists: [],
        departments: [],
        modifiers: [],
        variants: [],
        taxes: [],
        track: false
      },
      itemVariants: [],
      predefineColours: [
        { value: '16729344', colour: '#25960b' },
        { value: '11156336', colour: '#aba298' },
        { value: '16766720', colour: '#ffd700' },
        { value: '9437184', colour: '#90ee90' },
        { value: '13520127', colour: '#00ced1' },
        { value: '1973791', colour: '#1e90ff' },
        { value: '1973791', colour: '#c71585' },
        { value: '16729345', colour: '#ff4500' },
        { value: '16750464', colour: '#ff7800' },
        { value: '19371839', colour: '#00babd' },
        { value: '1973791', colour: '#1f93ff' },
        { value: '13028405', colour: '#c71585' }
      ],
      rules: {
        required: value => !!value || this.$t('validation.required')
      },
      loading: false,
      unitTypes: [],
      variantHeaders: [
        { text: this.$t('variant.table.title.variant'), value: 'name' },
        { text: this.$t('variant.table.title.unit_price'), value: 'unit_price' },
        { text: this.$t('variant.table.title.purchase_price'), value: 'purchase_price' },
        { text: this.$t('variant.table.title.code'), value: 'code' },
        { text: this.$t('variant.table.title.barcode'), value: 'barcode' },
        { text: this.$t('label.action'), value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.display
      },
      set (value) {
        if (!value) {
          this.$emit('close', false)
        }
      }
    },
    edit () {
      return this.item && this.item.id
    },
    edited () {
      return JSON.stringify(this.form) !== JSON.stringify(this.snapshot)
    }
  },
  watch: {
    display (val) {
      if (val) {
        this.open()
      }
    },
    'form.visual_type' (value) {
      if (value === 'image') {
        if (this.edit && this.item.colour) {
          this.form.colour = this.item.colour
        } else {
          this.form.colour = ''
        }
      } else {
        this.form.colour = this.predefineColours[0].colour
      }
    }
  },
  methods: {
    setColour (colour) {
      if (this.form.colour === colour.colour) {
        this.form.colour = ''
      } else {
        this.form.colour = colour.colour
      }
    },
    updateEditedItemVariant (itemVariantId, itemVariantModule) {
      this.editedItemVariant.id = itemVariantId
      this.editedItemVariant.module = itemVariantModule
    },
    updateItemVariants (data = null) {
      if (data !== null) {
        this.itemVariants = data
      } else {
        this.$itemService.getItemVariants(this.item.id)
          .then(data => {
            this.itemVariants = data
          })
      }
    },
    deleteAllVariants () {
      this.askQuestion(this.$t('variant.confirm.delete_all'), async () => {
        this.loading = true
        this.$itemService.deleteVariant(this.item.id)
          .then(data => {
            this.$emit('updated', data)
            this.loading = false
            this.notify(this.$t('variant.message.deleted'), this.$t('status.success'))
          })
      }, this.$t('variant.title.delete_all'), () => {
      }, 'error', { confirmButtonClass: 'el-button--danger' })
    },
    itemVariantDelete (id) {
      this.askQuestion(this.$t('variant.confirm.delete'), async () => {
        this.loading = true
        this.$itemService.deleteItemVariant(id)
          .then(data => {
            const index = this.itemVariants.findIndex(v => v.id === id)
            if (index > -1) {
              this.itemVariants.splice(index, 1)
            }
            this.loading = false
            this.notify(this.$t('variant.message.deleted'), this.$t('status.success'))
          })
      })
    },
    setSellingPrice (value) {
      this.form.unit_price = parseInt(value)
    },
    handleImageUpload (file) {
      if (file) {
        const reader = new FileReader()
        reader.onloadend = () => {
          this.form.image = reader.result
        }
        reader.readAsDataURL(file)
      } else {
        this.form.image = ''
      }
    },
    open () {
      this.fetchData()
      if (this.edit) {
        this.loadItemData()
      } else {
        this.form.colour = this.predefineColours[0].colour
      }
      this.snapshot = JSON.parse(JSON.stringify(this.form))
      this.$nextTick(() => {
        if (this.$refs.inputName) {
          this.$refs.inputName.focus()
        }
      })
    },
    loadItemData () {
      this.form.name = this.item.name
      this.form.description = this.item.description
      this.form.unit_price = this.item.unit_price
      this.form.purchase_price = this.item.purchase_price
      this.form.minimum_price = this.item.minimum_price
      this.form.unit_type = this.item.unit_type
      this.form.item_type = this.item.item_type
      this.form.visual_type = this.item.visual_type === 'colour' ? 'colour' : 'image'
      this.form.sold_by = this.item.sold_by !== undefined ? this.item.sold_by : 'each'
      this.form.image = this.item.image
      this.form.colour = this.item.colour
      this.form.barcode = this.item.barcode
      this.form.sellable = this.item.sellable !== undefined ? this.item.sellable : true
      this.form.reorder_level = this.item.reorder_level
      this.form.optimal_order = this.item.optimal_order
      this.form.category = this.item.category_id
      this.form.supplier = this.item.supplier_id
      this.form.price_lists = this.item.price_lists
      this.form.departments = this.item.departments
      this.form.modifiers = this.item.modifiers
      this.form.variants = []
      this.form.taxes = this.item.taxes
      this.form.track = this.item.tracked || false
    },
    handleClose () {
      this.$emit('close', false)
    },
    clearFields () {
      if (this.$refs.itemForm) {
        this.$refs.itemForm.reset()
      }
      this.form = {
        name: '',
        description: '',
        unit_price: null,
        purchase_price: null,
        minimum_price: null,
        unit_type: null,
        item_type: 'product',
        visual_type: 'colour',
        sold_by: 'each',
        image: '',
        colour: '',
        barcode: '',
        sellable: true,
        reorder_level: null,
        optimal_order: null,
        category: null,
        supplier: null,
        price_lists: [],
        departments: [],
        modifiers: [],
        variants: [],
        taxes: [],
        track: false
      }
      this.loading = false
    },
    toggleVariantDialog (value) {
      if (value === undefined) value = false
      this.variantDialog = value
    },
    newVariant (data) {
      if (data && data.length) {
        this.form.variants = data
      } else {
        this.form.variants = []
      }
    },
    updatedVariant (data) {
      if (data && data.length) {
        this.form.variants = data
      } else {
        this.form.variants = []
      }
    },
    updateItemModifier (value, modifier) {
      if (value) {
        this.$nextTick(_ => (this.form.modifiers.push(modifier)))
      } else {
        this.$nextTick(_ => {
          const index = this.form.modifiers.indexOf(this.form.modifiers.find(m => m.id === modifier.id))
          if (index > -1) {
            this.form.modifiers.splice(index, 1)
          }
        })
      }
    },
    async updateItemTax (value, tax) {
      if (value) {
        this.$nextTick(_ => (this.form.taxes.push(tax)))
      } else {
        this.$nextTick(_ => {
          const index = this.form.taxes.indexOf(this.form.taxes.find(m => m.id === tax.id))
          if (index > -1) {
            this.form.taxes.splice(index, 1)
          }
        })
      }
    },
    async submitForm () {
      if (this.$refs.itemForm.validate()) {
        try {
          await this.withLoading(async () => {
            this.askQuestion(this.$t(`item.confirm.${this.edit ? 'update' : 'create'}`), async () => {
              this.loading = true
              if (this.edit) {
                const data = await this.$itemService.updateItem(this.item.id, this.form)
                this.showSuccess(this.$t('item.message.updated'))
                this.$emit('updated', data)
              } else {
                const data = await this.$itemService.createItem(this.form)
                this.showSuccess(this.$t('item.message.created'))
                this.$emit('created', data)
              }
              this.$emit('close', false)
              this.clearFields()
            })
          }, 'loading')
        } catch (error) {
          console.log('Component caught error in saveItem:', error)
        } finally {
          this.loading = false
        }
      }
    },
    fetchSuppliers () {
      return new Promise((resolve, reject) => {
        this.$supplierService.getSuppliers()
          .then(resolve)
          .catch(reject)
      })
    },
    async fetchData () {
      this.$categoryService.getCategories()
        .then(data => {
          this.categories = data
        })
      this.suppliers = await this.fetchSuppliers()
      setTimeout(() => {
        this.$configService.getPriceLists()
          .then(data => {
            this.form.price_lists = data.map(pl => {
              let value = null
              if (this.edit) {
                const ipl = this.item.price_lists.find(ipl => ipl.id === pl.id)
                if (ipl && ipl.id) {
                  value = ipl.pivot.price
                }
              }
              return {
                id: pl.id,
                name: pl.name,
                value
              }
            })
          })
        if (this.edit) {
          this.$departmentService.getDepartmentsWithWarehouseItem(this.item.id)
            .then(data => {
              this.form.departments = data.map(d => {
                return {
                  id: d.id,
                  wh: d.warehouse ? d.warehouse.name : null,
                  name: d.name,
                  selling_price: d.warehouse && d.warehouse.items.length ? d.warehouse.items[0].pivot.selling_price : null
                }
              })
            })
        } else {
          this.$departmentService.getDepartmentsWithWarehouseItem()
            .then(data => (this.form.departments = data.map(d => ({
              id: d.id,
              wh: d.warehouse ? d.warehouse.name : null,
              name: d.name,
              selling_price: null
            }))))
        }
      }, 200)
      this.$itemService.getItemsUnitTypes()
        .then(data => (this.unitTypes = data))
      await this.$modifierService.getModifiers()
        .then(data => {
          this.modifiers = data.map(m => ({
            ...m,
            selected: this.item.modifiers ? !!this.item.modifiers.find(mm => m.id === mm.id) : false
          }))
        })
      await this.$taxService.getTaxes()
        .then(data => {
          this.taxes = data.map(t => ({
            ...t,
            selected: this.item.taxes ? !!this.item.taxes.find(tt => t.id === tt.id) : false
          }))
        })
      await this.updateItemVariants()
    }
  }
}
</script>

<style scoped>
.fixed-bottom-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: white;
}
</style>
